import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Eye, EyeOff, Lock, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/chalkApi";
import { ChalkLogoLockup } from "../components/chalk/Logo";

const ROLE_OPTIONS = [
  { id: "coach", label: "Coach", sub: "Head coach, assistant, or volunteer" },
  { id: "guardian", label: "Parent / Guardian", sub: "I received an invite" },
];

const ROLE_QUERY = ["coach", "guardian"];

// /start and /join/team flows return the user to where they began.
// Peek only: /start and /join/team clear the key themselves once they render.
function flowDest() {
  const t = sessionStorage.getItem("chalkos.afterLogin") || "";
  return t.startsWith("/start") || t.startsWith("/join/team") ? t : null;
}

export default function Login() {
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isSignup = location.pathname === "/signup" || params.get("mode") === "signup";
  const [tab, setTab] = useState(isSignup ? "signup" : "signin");
  const { signin, signup, consumeMagic, user } = useAuth();

  // Forms
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [firstName, setFirst] = useState("");
  const [lastName, setLast] = useState("");
  const [role, setRole] = useState(
    ROLE_QUERY.includes(params.get("role")) ? params.get("role") : "coach"
  );
  // Step 0 of signup: role selector. Skip when ?role= is passed (deep link from
  // a marketing CTA already picked a role).
  const initialRoleStep = (isSignup || params.get("mode") === "signup") && !params.get("role")
    ? "select" : "form";
  const [roleStep, setRoleStep] = useState(initialRoleStep);

  const [serverError, setServerError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Magic link
  const [showMagic, setShowMagic] = useState(false);
  const [magicEmail, setMagicEmail] = useState("");
  const [magicSent, setMagicSent] = useState(false);
  const [magicSentTo, setMagicSentTo] = useState("");

  // Auto-consume magic token if ?magic=
  useEffect(() => {
    const t = params.get("magic");
    if (t) {
      consumeMagic(t)
        .then(() => navigate(flowDest() || "/app"))
        .catch((e) => setServerError(e.message));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set when this screen's own sign-in/sign-up handler navigates, so the
  // "already signed in" redirect below doesn't override it.
  const authNavRef = useRef(false);
  // Already signed in -> /app
  useEffect(() => {
    if (user && !authNavRef.current) navigate(flowDest() || "/app", { replace: true });
  }, [user, navigate]);

  // Tab switching: instant, no page nav
  function switchTab(next) {
    setTab(next);
    setServerError(null);
    setFieldErrors({});
    setShowMagic(false);
    setMagicSent(false);
    const url = next === "signup" ? "/signup" : "/login";
    window.history.pushState({}, "", url);
    setTimeout(() => {
      const f = document.querySelector("[data-first-field]");
      if (f) f.focus();
    }, 50);
  }

  async function onSignin(e) {
    e.preventDefault();
    setServerError(null);
    const fe = {};
    if (!email || !/.+@.+\..+/.test(email)) fe.email = "Enter a valid email address";
    if (!password) fe.password = "Password is required";
    if (Object.keys(fe).length) return setFieldErrors(fe);
    setLoading(true);
    try {
      authNavRef.current = true;
      await signin(email.trim().toLowerCase(), password);
      const target = flowDest() || sessionStorage.getItem("chalkos.afterLogin") || "/app";
      if (!flowDest()) sessionStorage.removeItem("chalkos.afterLogin");
      navigate(target);
    } catch (e) {
      authNavRef.current = false;
      setServerError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function onSignup(e) {
    e.preventDefault();
    setServerError(null);
    const fe = {};
    if (!firstName) fe.firstName = "First name is required";
    if (!lastName) fe.lastName = "Last name is required";
    if (!email || !/.+@.+\..+/.test(email)) fe.email = "Enter a valid email address";
    if (!password || password.length < 8) fe.password = "At least 8 characters";
    if (Object.keys(fe).length) return setFieldErrors(fe);
    setLoading(true);
    try {
      authNavRef.current = true;
      const u = await signup({
        first_name: firstName, last_name: lastName, email: email.trim().toLowerCase(),
        password, role,
      });
      // Funnel: coach → Chalkboard checkout on first login (after onboarding).
      // Parent → optional Clipboard+ upsell.
      try {
        if (flowDest()) { /* /start or /join/team flow: no auto-checkout */ }
        else if (role === "coach") sessionStorage.setItem("chalk_wants_checkout", "chalkboard");
        else if (role === "guardian") sessionStorage.setItem("chalk_wants_checkout", "clipboard_plus");
      } catch { /* ignore quota */ }
      const dest = flowDest() || (u.role === "coach" || u.role === "league_admin" ? "/onboarding" : "/app");
      navigate(dest);
    } catch (e) {
      authNavRef.current = false;
      setServerError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function sendMagic() {
    if (!magicEmail || !/.+@.+\..+/.test(magicEmail)) {
      toast.error("Enter a valid email.");
      return;
    }
    try {
      await api.magicLink(magicEmail.trim().toLowerCase());
      setMagicSent(true);
      setMagicSentTo(magicEmail.trim().toLowerCase());
    } catch (e) {
      toast.error(e.message);
    }
  }

  return (
    <div className="chalk-min-h-screen" style={{ display: "flex", background: "var(--chalk-bg)" }}>
      {/* Left brand panel — desktop only */}
      <BrandPanel />

      {/* Right form panel */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 20px" }}>
        <div className="chalk-fadeup chalk-fadeup-form" style={{ width: "100%", maxWidth: 400 }}>
          {/* Mobile-only top wordmark */}
          <div className="lg:hidden" style={{ marginBottom: 18, display: "flex", justifyContent: "center" }}>
            <ChalkLogoLockup size={26} color="var(--chalk-board)" />
          </div>

          {/* Tab pill */}
          <div
            role="tablist"
            style={{
              background: "#fff", borderRadius: 999, padding: 4,
              display: "flex", gap: 4, border: "0.5px solid var(--chalk-border)", marginBottom: 22,
            }}
          >
            {[
              { id: "signin", label: "Sign in" },
              { id: "signup", label: "Create account" },
            ].map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => switchTab(t.id)}
                style={{
                  flex: 1, padding: "9px 8px", borderRadius: 999, border: "none",
                  cursor: "pointer", fontSize: 13.5,
                  background: tab === t.id ? "var(--chalk-board)" : "transparent",
                  color: tab === t.id ? "var(--chalk-white)" : "var(--chalk-muted)",
                  fontWeight: tab === t.id ? 500 : 400,
                  transition: "background 120ms ease, color 120ms ease",
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Headings */}
          {tab === "signin" ? (
            <>
              <h1 className="font-serif-display" style={{ fontSize: 28, margin: 0, color: "var(--chalk-board)" }}>
                Welcome back.
              </h1>
              <p style={{ fontSize: 14, color: "var(--chalk-muted)", marginTop: 6 }}>
                Sign in to your ChalkOS account to continue.
              </p>
            </>
          ) : (
            <>
              <h1 className="font-serif-display" style={{ fontSize: 28, margin: 0, color: "var(--chalk-board)" }}>
                Draw it up.
              </h1>
              <p style={{ fontSize: 14, color: "var(--chalk-muted)", marginTop: 6 }}>
                Create your ChalkOS account. Free to start, no card required.
              </p>
            </>
          )}

          {serverError && (
            <div
              role="alert"
              aria-live="assertive"
              data-testid="server-error"
              style={{
                marginTop: 16, padding: "10px 12px",
                background: "var(--chalk-red-bg)", color: "var(--chalk-red)",
                borderRadius: 10, fontSize: 13,
              }}
            >
              {serverError}
            </div>
          )}

          {tab === "signin" ? (
            <SigninForm
              email={email} setEmail={setEmail}
              password={password} setPassword={setPassword}
              showPw={showPw} setShowPw={setShowPw}
              loading={loading}
              fieldErrors={fieldErrors}
              setServerError={setServerError}
              setFieldErrors={setFieldErrors}
              onSubmit={onSignin}
            />
          ) : roleStep === "select" ? (
            <RoleSelector
              onPick={(r) => { setRole(r); setRoleStep("form"); }}
            />
          ) : (
            <SignupForm
              role={role} setRole={setRole}
              firstName={firstName} setFirst={setFirst}
              lastName={lastName} setLast={setLast}
              email={email} setEmail={setEmail}
              password={password} setPassword={setPassword}
              showPw={showPw} setShowPw={setShowPw}
              loading={loading}
              fieldErrors={fieldErrors}
              setServerError={setServerError}
              setFieldErrors={setFieldErrors}
              onSubmit={onSignup}
              onChangeRole={() => setRoleStep("select")}
            />
          )}

          {tab === "signin" && (
            <MagicLinkBlock
              show={showMagic} setShow={setShowMagic}
              email={magicEmail} setEmail={setMagicEmail}
              sent={magicSent} sentTo={magicSentTo} send={sendMagic}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function BrandPanel() {
  return (
    <div
      className="hidden lg:flex"
      style={{
        flex: "0 0 50%", background: "var(--chalk-board)", color: "var(--chalk-white)",
        padding: "40px 56px", position: "relative", overflow: "hidden",
        flexDirection: "column", justifyContent: "space-between",
      }}
    >
      {/* Faint chalk lines */}
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {[28, 52, 76].map((top) => (
          <div key={top} style={{ position: "absolute", top: `${top}%`, left: 0, right: 0, height: 1, background: "var(--chalk-white)", opacity: 0.06 }} />
        ))}
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(60% 50% at 30% 30%, rgba(245,240,232,0.04), transparent 60%)" }} />
      </div>

      <div className="chalk-fadeup chalk-fadeup-delay-1" style={{ position: "relative" }}>
        <ChalkLogoLockup size={36} color="var(--chalk-white)" />
      </div>

      <div className="chalk-fadeup chalk-fadeup-delay-2" style={{ position: "relative", maxWidth: 460 }}>
        <h2 className="font-serif-display" style={{ fontSize: 56, lineHeight: 0.95, margin: 0, color: "var(--chalk-white)" }}>
          The board is ready<br />when you are.
        </h2>
        <div style={{ fontFamily: "'Permanent Marker', cursive", fontSize: 28, color: "var(--green-chalk)", marginTop: 10, transform: "rotate(-2deg)", display: "inline-block" }}>
          Always.
        </div>
        <p style={{ fontFamily: "'Barlow', system-ui, sans-serif", fontSize: 15, color: "rgba(255,255,255,0.72)", marginTop: 18, fontWeight: 400 }}>
          Your team, your schedule, your families — all in one place.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 22 }}>
          {["Coach", "Parent / Guardian", "Team Mom", "League Admin"].map((r) => (
            <span key={r} style={{
              padding: "5px 10px", fontSize: 12,
              background: "rgba(245,240,232,0.06)",
              border: "0.5px solid rgba(245,240,232,0.10)",
              borderRadius: 999, color: "rgba(245,240,232,0.85)",
            }}>{r}</span>
          ))}
        </div>

        <div style={{ marginTop: 36, display: "grid", gap: 16 }}>
          {[
            { t: "Your Playbook is waiting.", b: "Schedule, RSVPs, conflicts — one tap." },
            { t: "Families stay connected.", b: "Chalk Talk replaces chaotic group texts." },
            { t: "Chalk Talk, live.", b: "Coach announcements with real delivery receipts." },
          ].map((row) => (
            <div key={row.t}>
              <div style={{ fontWeight: 500, color: "var(--chalk-white)", fontSize: 14 }}>{row.t}</div>
              <div style={{ fontSize: 13, color: "rgba(245,240,232,0.55)", marginTop: 2, fontWeight: 300 }}>{row.b}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="chalk-fadeup chalk-fadeup-delay-3" style={{ position: "relative", fontSize: 12, color: "rgba(245,240,232,0.40)" }}>
        GetChalkOS.com
      </div>
    </div>
  );
}

function SigninForm({ email, setEmail, password, setPassword, showPw, setShowPw, loading, fieldErrors, setServerError, setFieldErrors, onSubmit }) {
  return (
    <form onSubmit={onSubmit} style={{ marginTop: 18, display: "grid", gap: 12 }}>
      <Field
        icon={<Mail size={16} aria-hidden="true" />}
        label="Email address"
        type="email"
        autoComplete="email"
        placeholder="coach@example.com"
        value={email}
        onChange={(v) => { setEmail(v); setServerError(null); setFieldErrors((fe) => ({ ...fe, email: undefined })); }}
        error={fieldErrors.email}
        testId="signin-email"
        firstField
      />
      <Field
        icon={<Lock size={16} aria-hidden="true" />}
        label="Password"
        type={showPw ? "text" : "password"}
        autoComplete="current-password"
        placeholder="Your password"
        value={password}
        onChange={(v) => { setPassword(v); setServerError(null); setFieldErrors((fe) => ({ ...fe, password: undefined })); }}
        error={fieldErrors.password}
        testId="signin-password"
        trailing={
          <button
            type="button"
            onClick={() => setShowPw(!showPw)}
            aria-label={showPw ? "Hide password" : "Show password"}
            style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--chalk-muted)", padding: 6 }}
          >
            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        }
      />
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: -4 }}>
        <Link to="/forgot" style={{ color: "var(--chalk-green)", fontSize: 13, textDecoration: "none", fontWeight: 500 }} data-testid="forgot-link">
          Forgot password?
        </Link>
      </div>
      <button type="submit" className="chalk-btn-primary" disabled={loading} data-testid="signin-submit">
        {loading ? "Signing in…" : (<>Sign in <ArrowRight size={16} aria-hidden="true" /></>)}
      </button>
      {/* Sign-in-only helper for parents/players who got a code from a coach.
          We don't open the sheet here because they have to be signed in for
          the request to land on the coach — we just point them at the right
          surface post-sign-in. */}
      <div style={{ fontSize: 12, color: "var(--chalk-muted)", textAlign: "center", marginTop: 8 }} data-testid="signin-join-hint">
        Have a team code? <strong style={{ color: "var(--chalk-board)" }}>Sign in</strong> first, then tap “Join a team with a code” on the More tab.
      </div>
    </form>
  );
}

function SignupForm({ role, setRole, firstName, setFirst, lastName, setLast, email, setEmail, password, setPassword, showPw, setShowPw, loading, fieldErrors, setServerError, setFieldErrors, onSubmit, onChangeRole }) {
  const strength = passwordStrength(password);
  const strengthMeta = {
    too_short: { label: "Too short", color: "var(--chalk-red)", segments: 1 },
    weak:      { label: "Weak",      color: "var(--chalk-amber)", segments: 2 },
    good:      { label: "Good",      color: "var(--chalk-green)", segments: 3 },
    strong:    { label: "Strong",    color: "var(--chalk-green)", segments: 4 },
  }[strength];

  const roleLabel = (ROLE_OPTIONS.find((r) => r.id === role) || {}).label || role;

  return (
    <form onSubmit={onSubmit} style={{ marginTop: 18, display: "grid", gap: 12 }}>
      {onChangeRole && (
        <div data-testid="signup-role-chip" style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          background: "#FBF8F2", border: "0.5px solid var(--chalk-border)",
          borderRadius: 12, padding: "10px 14px",
        }}>
          <div>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-muted)" }}>Account type</div>
            <div style={{ fontSize: 14, fontWeight: 500, color: "var(--chalk-board)" }}>{roleLabel}</div>
          </div>
          <button type="button" data-testid="signup-change-role" onClick={onChangeRole}
                  style={{
                    background: "transparent", border: "none",
                    color: "var(--chalk-green)", fontSize: 12, cursor: "pointer",
                  }}>
            Change
          </button>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field icon={null} firstField label="First name" value={firstName} onChange={setFirst} placeholder="First" error={fieldErrors.firstName} testId="signup-first" />
        <Field icon={null} label="Last name" value={lastName} onChange={setLast} placeholder="Last" error={fieldErrors.lastName} testId="signup-last" />
      </div>

      <Field
        icon={<Mail size={16} aria-hidden="true" />}
        label="Email address"
        type="email"
        autoComplete="email"
        placeholder="you@example.com"
        value={email}
        onChange={(v) => { setEmail(v); setServerError(null); setFieldErrors((fe) => ({ ...fe, email: undefined })); }}
        error={fieldErrors.email}
        testId="signup-email"
      />
      <div>
        <Field
          icon={<Lock size={16} aria-hidden="true" />}
          label="Password"
          type={showPw ? "text" : "password"}
          autoComplete="new-password"
          placeholder="At least 8 characters"
          value={password}
          onChange={(v) => { setPassword(v); setServerError(null); setFieldErrors((fe) => ({ ...fe, password: undefined })); }}
          error={fieldErrors.password}
          testId="signup-password"
          trailing={
            <button type="button" onClick={() => setShowPw(!showPw)} aria-label={showPw ? "Hide password" : "Show password"} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--chalk-muted)", padding: 6 }}>
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          }
        />
        {password && (
          <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8 }} aria-live="polite">
            <div style={{ display: "flex", gap: 4, flex: 1 }}>
              {[0, 1, 2, 3].map((i) => (
                <div key={i} style={{
                  flex: 1, height: 4, borderRadius: 2,
                  background: i < strengthMeta.segments ? strengthMeta.color : "rgba(0,0,0,0.08)",
                }} />
              ))}
            </div>
            <div style={{ fontSize: 11, color: strengthMeta.color, fontWeight: 500, minWidth: 60, textAlign: "right" }}>
              {strengthMeta.label}
            </div>
          </div>
        )}
      </div>

      <button type="submit" className="chalk-btn-primary" disabled={loading} data-testid="signup-submit">
        {loading ? "Creating account…" : (<>Create account <ArrowRight size={16} aria-hidden="true" /></>)}
      </button>

      <p style={{ fontSize: 11, color: "var(--chalk-muted-2)", marginTop: 4 }} data-testid="signup-consent">
        By creating an account you agree to ChalkOS's{" "}
        <Link to="/terms" target="_blank" rel="noopener" data-testid="signup-terms-link" style={{ color: "inherit", textDecoration: "underline" }}>
          Terms of Service
        </Link>{" "}and{" "}
        <Link to="/privacy" target="_blank" rel="noopener" data-testid="signup-privacy-link" style={{ color: "inherit", textDecoration: "underline" }}>
          Privacy Policy
        </Link>.
      </p>
    </form>
  );
}

function passwordStrength(pw) {
  if (!pw) return "too_short";
  if (pw.length < 8) return "too_short";
  let score = 0;
  if (/[a-z]/.test(pw)) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^a-zA-Z0-9]/.test(pw)) score++;
  if (pw.length >= 12 && score >= 3) return "strong";
  if (score >= 3) return "good";
  return "weak";
}

function Field({ icon, label, type = "text", placeholder, value, onChange, error, trailing, autoComplete, firstField, testId }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 12, color: "var(--chalk-muted)", marginBottom: 6 }}>{label}</label>
      <div style={{ position: "relative" }}>
        {icon && (
          <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--chalk-muted-2)", display: "flex" }}>
            {icon}
          </span>
        )}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          data-first-field={firstField || undefined}
          data-testid={testId}
          aria-invalid={!!error}
          aria-label={label}
          className={`chalk-input ${error ? "has-error" : ""}`}
          style={{
            paddingLeft: icon ? 42 : 14,
            paddingRight: trailing ? 44 : 14,
          }}
        />
        {trailing && (
          <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)" }}>{trailing}</span>
        )}
      </div>
      {error && <div role="alert" style={{ fontSize: 12, color: "var(--chalk-red)", marginTop: 4 }}>{error}</div>}
    </div>
  );
}

function MagicLinkBlock({ show, setShow, email, setEmail, sent, sentTo, send }) {
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <div style={{ flex: 1, height: 1, background: "var(--chalk-border)" }} />
        <span style={{ fontSize: 11, color: "var(--chalk-muted)" }}>or sign in with a magic link</span>
        <div style={{ flex: 1, height: 1, background: "var(--chalk-border)" }} />
      </div>
      {!show && !sent && (
        <button onClick={() => setShow(true)} className="chalk-btn-ghost" data-testid="magic-open">
          <Sparkles size={16} aria-hidden="true" /> Send me a magic link
        </button>
      )}
      {show && !sent && (
        <div style={{ display: "grid", gap: 10 }}>
          <input
            type="email"
            placeholder="Enter your email address"
            className="chalk-input"
            style={{ paddingLeft: 14 }}
            data-testid="magic-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <button onClick={send} className="chalk-btn-primary" data-testid="magic-send">
            Send magic link <ArrowRight size={16} aria-hidden="true" />
          </button>
        </div>
      )}
      {sent && (
        <div
          className="chalk-card"
          style={{ borderColor: "rgba(46,125,82,0.30)" }}
          data-testid="magic-success"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--chalk-green)" }}>
            <CheckCircle2 size={18} aria-hidden="true" />
            <div style={{ fontSize: 15, fontWeight: 500 }}>Check your inbox</div>
          </div>
          <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 6 }}>
            We sent a sign-in link to {sentTo}. It expires in 15 minutes.
          </div>
        </div>
      )}
    </div>
  );
}


// Step 0 of signup — "Who's signing up?" — sets role + downstream funnel.
function RoleSelector({ onPick }) {
  const ROLES = [
    {
      id: "coach",
      title: "I'm a coach",
      sub: "Run your team — practices, games, Chalk Talk, Erase and Redraw.",
      footnote: "Recommended: The Chalkboard · $11.99/mo",
      accent: "var(--chalk-green)",
    },
    {
      id: "guardian",
      title: "I'm a parent",
      sub: "See what's next for my kids and RSVP in one tap.",
      footnote: "Free — upgrade to Clipboard+ anytime.",
      accent: "var(--chalk-board)",
    },
    {
      id: "league_admin",
      title: "I run a league",
      sub: "League accounts are set up by our team, then you manage every team and broadcast leaguewide.",
      footnote: "Contact us for League OS",
      accent: "var(--chalk-board)",
      href: "mailto:support@getchalkos.com?subject=League OS",
    },
  ];
  return (
    <div data-testid="role-selector" style={{ marginTop: 18 }}>
      <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginBottom: 14 }}>
        Who's signing up?
      </div>
      <div style={{ display: "grid", gap: 10 }} role="radiogroup" aria-label="Account type">
        {ROLES.map((r) => (
          <button
            key={r.id}
            type="button"
            role="radio"
            aria-checked={false}
            data-testid={`role-card-${r.id}`}
            onClick={() => (r.href ? window.location.assign(r.href) : onPick(r.id))}
            style={{
              textAlign: "left", padding: "16px 16px",
              background: "#fff", border: "0.5px solid var(--chalk-border)",
              borderRadius: 14, cursor: "pointer",
              transition: "transform 120ms ease, border-color 120ms ease, box-shadow 120ms ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = r.accent; e.currentTarget.style.transform = "translateY(-1px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--chalk-border)"; e.currentTarget.style.transform = "translateY(0)"; }}
          >
            <div style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 20, color: "var(--chalk-board)" }}>{r.title}</div>
            <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 6, lineHeight: 1.4 }}>{r.sub}</div>
            <div style={{ fontSize: 11, color: r.accent, marginTop: 8, letterSpacing: "0.04em", textTransform: "uppercase" }}>
              {r.footnote}
            </div>
          </button>
        ))}
      </div>
      <div style={{ marginTop: 14, fontSize: 12, color: "var(--chalk-muted)", textAlign: "center" }}>
        You can change this later in your account.
      </div>
    </div>
  );
}
